import React, { memo, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

export type JourneyIconMotion = 'gain' | 'loss' | 'balance' | 'none';

type Props = {
  motion: JourneyIconMotion;
  /** Slightly stronger motion when the row is selected. */
  active?: boolean;
  children: React.ReactNode;
};

/**
 * Soft looping glyph motion for journey goals.
 * Always mounts the same Animated.View (stable layout / hooks).
 */
export const JourneyIconMotionWrap = memo(function JourneyIconMotionWrap({
  motion,
  active = false,
  children,
}: Props) {
  const phase = useSharedValue(0);

  useEffect(() => {
    if (motion === 'none') {
      cancelAnimation(phase);
      phase.value = withTiming(0, { duration: 200 });
      return;
    }
    const duration = motion === 'balance' ? 1600 : 1300;
    phase.value = 0;
    phase.value = withRepeat(
      withTiming(1, { duration, easing: Easing.inOut(Easing.sin) }),
      -1,
      true,
    );
    return () => {
      cancelAnimation(phase);
    };
  }, [motion, phase]);

  const style = useAnimatedStyle(() => {
    if (motion === 'none') {
      return { transform: [{ translateY: 0 }, { rotate: '0deg' }, { scale: 1 }] };
    }
    const amp = active ? 1.15 : 1;
    const p = phase.value;
    if (motion === 'gain') {
      // Rise and ease back — upward bias
      return {
        transform: [
          { translateY: (0.5 - p) * 4.5 * amp },
          { scale: 1 + p * 0.05 * amp },
        ],
      };
    }
    if (motion === 'loss') {
      // Settle downward
      return {
        transform: [
          { translateY: (p - 0.5) * 4.5 * amp },
          { scale: 1 + (1 - p) * 0.04 * amp },
        ],
      };
    }
    // balance — gentle rock
    return {
      transform: [
        { rotate: `${(p - 0.5) * 12 * amp}deg` },
        { scale: 1 + Math.sin(p * Math.PI) * 0.03 * amp },
      ],
    };
  });

  return <Animated.View style={[styles.wrap, style]}>{children}</Animated.View>;
});

const styles = StyleSheet.create({
  wrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
